import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1616671276441-2f2c277b8bf9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxmaW5lJTIwZGluaW5nJTIwcGxhdGVkJTIwZ291cm1ldCUyMGZvb2QlMjBjYXRlcmluZyUyMGhlcm98ZW58MHx8fHwhfHwxNzkyNzk3NTU5fDA&ixlib=rb-4.1.0&q=85";

const LINES = [
  { text: "The art of", italic: false },
  { text: "the gathering,", italic: true },
  { text: "perfected.", italic: false },
];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-16 sm:pt-40 lg:pb-24"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-8">
        <motion.div style={{ opacity: fade }}>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-ink-mute"
            data-testid="hero-overline"
          >
            <span className="inline-block h-px w-10 bg-gold" />
            Kasey Beaumont — Private Catering
          </motion.p>

          <h1 className="font-serif text-4xl leading-[1.08] tracking-tight text-noir sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className={`block ${line.italic ? "italic text-truffle" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-8 max-w-md text-base leading-relaxed text-ink-soft sm:text-lg"
            data-testid="hero-subcopy"
          >
            Architectural gastronomy for weddings, estates and boardrooms —
            menus composed around the season, plated like sculpture, served
            without a seam.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="mailto:kas@kaseybeaumont.com?subject=Catering%20Enquiry%20—%20kaseybeaumont.com"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-noir px-7 py-3.5 text-[13px] font-medium uppercase tracking-[0.16em] text-bone transition-colors duration-300 hover:bg-truffle"
            >
              Request Consultation
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#menus"
              data-testid="hero-secondary-link"
              className="group inline-flex items-center gap-2 rounded-full border border-noir/20 px-7 py-3.5 text-[13px] font-medium uppercase tracking-[0.16em] text-noir transition-colors duration-300 hover:border-gold hover:text-truffle"
            >
              Explore the Menus
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-mute"
            data-testid="hero-stats"
          >
            <span>Est. 2014</span>
            <span className="h-1 w-1 rounded-full bg-gold" />
            <span>300+ Events Staged</span>
            <span className="h-1 w-1 rounded-full bg-gold" />
            <span>Farm-Direct Sourcing</span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div className="spotlight absolute -inset-10 -z-10" aria-hidden="true" />
          <div
            className="pointer-events-none absolute -top-8 -right-6 h-40 w-40 rounded-full border border-gold/40"
            aria-hidden="true"
          />
          <motion.div style={{ y: imgY }} className="arch-frame overflow-hidden shadow-[0_40px_80px_-30px_rgba(26,25,23,0.35)]">
            <img
              src={HERO_IMG}
              alt="Gourmet fine dining dish on white ceramic plate with herbs and dark reduction"
              data-testid="hero-dish-image"
              className="aspect-[4/5] w-full scale-105 object-cover"
            />
          </motion.div>
          <motion.div
            style={{ y: cardY }}
            data-testid="hero-dish-caption"
            className="absolute -bottom-6 -left-4 rounded-2xl border border-noir/10 bg-bone/85 px-5 py-4 shadow-lg backdrop-blur-xl sm:-left-10"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mute">
              Signature Course No. 01
            </p>
            <p className="mt-1 font-serif text-xl italic text-noir">Ember &amp; Ash</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
